// @flow
import env from '../../environment';
import {deleteCreator, getCreator, postCreator, putCreator} from '../../http';

import type {
  ProductSection,
  ProductOptionGroupConfig,
  StoreProductOptionType,
} from '../../entities';

export const endpoints = Object.freeze({
  GET_PRODUCTS: `${env.baseApiUrl}/winehouse/admin/product`,
  FETCH_PRODUCT: `${env.baseApiUrl}/im/admin/product/:id`,
  CREATE_PRODUCT: `${env.baseApiUrl}/im/admin/product`,
  EDIT_PRODUCT: `${env.baseApiUrl}/im/admin/product/:id`,
  EDIT_PRODUCT_BULK: `${env.baseApiUrl}/im/admin/product/bulk`,
  DELETE_PRODUCT: `${env.baseApiUrl}/im/admin/product/:id`,
  /*TODO: add legacy endpoint*/
  SORT_PRODUCT: `${env.baseApiUrl}/im/admin/product/:id/number`,
  ADD_PRODUCT_IMAGE: `${env.baseApiUrl}/im/admin/product/:id/images`,
  SAVE_PRODUCT_IMAGE: `${env.baseApiUrl}/im/admin/product/:id/image/:number`,
  DELETE_PRODUCT_IMAGE: `${env.baseApiUrl}/im/admin/product/:id/image/:number`,
  SAVE_PRODUCT_VARIANTS: `${env.baseApiUrl}/im/admin/product/:id/variant`,
  EDIT_PRODUCT_VARIANT: `${env.baseApiUrl}/im/admin/product/:id/variant/:variantId`,
  GET_PRODUCTS_SPREADSHEET: `${env.baseApiUrl}/im/admin/product/feed/tiendada`,
  GET_CREATE_PRODUCTS_SPREADSHEET: `${env.baseApiUrl}/im/admin/batch-product/template/create`,
  BATCH_UPDATE_PRODUCTS: `${env.baseApiUrl}/im/admin/product/import`,
  BATCH_CREATE_PRODUCTS: `${env.baseApiUrl}/im/admin/batch-product`,
  GET_FB_FEED_TOKEN: `${env.baseApiUrl}/im/admin/product/feed/token`,
  FB_FEED_LINK: `${env.baseApiUrl}/im/admin/product/feed/facebook`,
  ADD_PRODUCTS_CATALOGUE: `${env.baseApiUrl}/im/admin/product/build-product-catalogue`,
  GET_PRODUCTS_CATALOGUE: `${env.baseApiUrl}/im/admin/product/product-catalogue`,

  GET_METADATA_SETTING: `${env.baseApiUrl}/im/admin/product/:id/seo-setting`,
  GET_PRODUCT_SPECIFICATIONS: `${env.baseApiUrl}/im/admin/product/:id/specifications`,
  GET_RELATED_PRODUCTS: `${env.baseApiUrl}/im/admin/product/:id/related-products-setting`,
  GET_PRODUCT_SIZE_CHARTS: `${env.baseApiUrl}/im/admin/product/:id/size-chart`,

  GET_PRODUCT_TAXES: `${env.baseApiUrl}/im/admin/tax/product/:id`,
  GET_TAXES: `${env.baseApiUrl}/im/admin/tax`,

  GET_PRODUCT_VARIANT_OPTIONS: `${env.baseApiUrl}/im/admin/product-variant-option`,
  SAVE_PRODUCT_VARIANT_OPTION: `${env.baseApiUrl}/im/admin/product-variant-option`,
  EDIT_PRODUCT_VARIANT_OPTION: `${env.baseApiUrl}/im/admin/product-variant-option/:id`,
  GET_PRODUCT_VARIANT_OPTION: `${env.baseApiUrl}/im/admin/product-variant-option/:id`,

  // SECTIONS
  GET_PRODUCT_SECTIONS: `${env.baseApiUrl}/im/admin/product/:id/section`,

  // TAXES
  SET_PRODUCT_TAX: `${env.baseApiUrl}/im/admin/tax/:taxId/product`,
});

export type RelatedProducts = {
  enabled: boolean,
  criteria?: string,
  products?: string[],
  productCategories?: string[],
  productSubCategories?: string[],
};

export type VariantRequest = {
  imageId?: string | number,
  options?: {[key: string]: string},
  price?: string,
  offerPrice?: string,
  enabledBulkPrice?: boolean,
  stock?: number,
  sku?: string,
  enabled?: boolean,
};

export type ProductVariantsRequest = {
  options: {id: string, values: string[]}[],
  variants: VariantRequest[],
};

export type ProductSectionRequest = (ProductSection & {file?: File})[];
export type ProductMetadataSettingRequest = {
  enabled: boolean,
  title?: string,
  description?: string,
};

export type ProductOptionGroupOptionRequest = {
  storeProductOptionId: string,
  enabled?: boolean,
  useDefault?: boolean,
  price?: string,
  number: number,
};

export type ProductOptionGroupRequest = {
  storeProductOptionGroupId: string,
  enabled?: boolean,
  required?: boolean,
  fieldType?: StoreProductOptionType,
  config?: ProductOptionGroupConfig,
  options?: ProductOptionGroupOptionRequest[],
  number: number,
};

export type ProductOptionRequest = {
  enabled: boolean,
  groups: ProductOptionGroupRequest[],
};

export type ProductRequest = {
  id?: string,
  name?: string,
  videoUrl?: string,
  videoSocialMediaTypeId?: string,
  // cost?: string,
  description?: string,
  price?: string,
  offerPriceSetting?: {
    enabled?: boolean,
    offerPrice?: string,
  },

  imageFile?: File,
  categories?: string[],
  subCategories?: string[],
  subCategoryItems?: string[],
  enabled?: boolean,
  sku?: string,
  enabledProductVariants?: boolean,
  productVariants?: ProductVariantsRequest,
  spotlight?: boolean,
  specificationSetting?: {
    enabled?: boolean,
    specifications?: {name: string, description: string}[],
  },
  stockSetting?: {
    enabled?: boolean,
    stock?: number,
  },
  productVariantSetting?: {
    enabled?: boolean,
    options?: {id: string, values: string[]}[],
    variants?: {
      enabled: boolean,
      imageId: string,
      price: string,
      offerPrice?: string,
      combination: {[key: string]: string},
    }[],
  },
  bulkPriceSetting?: {
    enabled?: boolean,
    enabledMixAndMatch?: boolean,
    bulkPriceLines?: any,
  },
  seoSetting?: ProductMetadataSettingRequest,
  relatedProductSetting?: RelatedProducts,
  productSectionRequest?: ProductSectionRequest,
  productOptionsSetting?: ProductOptionRequest,

  metadataSetting?: {
    priceDisplay?: {
      mode?: 'PRICE' | 'LABEL',
      label?: string,
      url?: string,
    },
  },
};

export type EditProductVariantRequest = {
  productId: string,
  variantId: string,
  request: {
    price?: string,
    offerPrice?: string,
    enabledBulkPrice?: boolean,
    stock?: number,
    sku?: string,
    enabled?: boolean,
  },
};

export const getProductsApi = getCreator(endpoints.GET_PRODUCTS);
export const fetchProductApi = getCreator(endpoints.FETCH_PRODUCT);
export const createProductApi = postCreator(endpoints.CREATE_PRODUCT);
export const editProductApi = putCreator(endpoints.EDIT_PRODUCT);
export const editProductBulkApi = putCreator(endpoints.EDIT_PRODUCT_BULK);
export const deleteProductApi = deleteCreator(endpoints.DELETE_PRODUCT);
export const sortProductApi = putCreator(endpoints.SORT_PRODUCT);
export const addProductImageApi = postCreator(endpoints.ADD_PRODUCT_IMAGE);
export const saveProductImageApi = putCreator(endpoints.SAVE_PRODUCT_IMAGE);
export const saveProductVariantsApi = postCreator(
  endpoints.SAVE_PRODUCT_VARIANTS
);
export const editProductVariantApi = putCreator(endpoints.EDIT_PRODUCT_VARIANT);
export const getProductsCSVApi = getCreator(endpoints.GET_PRODUCTS_SPREADSHEET);
export const batchUpdateProductsApi = postCreator(
  endpoints.BATCH_UPDATE_PRODUCTS
);
export const batchCreateProductsApi = postCreator(
  endpoints.BATCH_CREATE_PRODUCTS
);
export const getFbFeedTokenApi = postCreator(endpoints.GET_FB_FEED_TOKEN);
export const getProductsCatalogueApi = getCreator(
  endpoints.GET_PRODUCTS_CATALOGUE
);
export const addProductsCatalogueApi = postCreator(
  endpoints.ADD_PRODUCTS_CATALOGUE
);

export const getProductMetadataSettingApi = getCreator(
  endpoints.GET_METADATA_SETTING
);
export const getProductSpecificationsApi = getCreator(
  endpoints.GET_PRODUCT_SPECIFICATIONS
);
export const getRelatedProductsSettingApi = getCreator(
  endpoints.GET_RELATED_PRODUCTS
);
export const getProductSizeChartsApi = getCreator(
  endpoints.GET_PRODUCT_SIZE_CHARTS
);

export const getProductTaxesApi = getCreator(endpoints.GET_PRODUCT_TAXES);
export const getTaxesApi = getCreator(endpoints.GET_TAXES);

export const saveProductVariantOptionApi = postCreator(
  endpoints.SAVE_PRODUCT_VARIANT_OPTION
);

export const getProductVariantOptionsApi = getCreator(
  endpoints.GET_PRODUCT_VARIANT_OPTIONS
);

export const getProductVariantOptionApi = getCreator(
  endpoints.GET_PRODUCT_VARIANT_OPTION
);

export const updateProductVariantOptionApi = putCreator(
  endpoints.GET_PRODUCT_VARIANT_OPTION
);

//sections
export const saveProductSectionsApi = postCreator(
  endpoints.GET_PRODUCT_SECTIONS
);
export const getProductSectionsApi = getCreator(endpoints.GET_PRODUCT_SECTIONS);

//taxes
export const setProductTaxApi = postCreator(endpoints.SET_PRODUCT_TAX);
